import { useState } from "react";
import { Form, Link, useNavigation } from "react-router-dom";
import {
  Eye,
  EyeOff,
  Terminal,
  Monitor,
  Users,
  Building,
  Code,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";

export default function Login() {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const navigation = useNavigation();
  const isSubmitting = navigation.state === "submitting";

  return (
    <div className="w-full max-w-6xl bg-white rounded-2xl shadow-2xl overflow-hidden">
      <div className="grid lg:grid-cols-2 min-h-[600px]">
        {/* Login Form Section */}
        <div className="flex flex-col justify-center p-8 lg:p-12">
          <div className="w-full max-w-md mx-auto">
            <div className="text-center mb-8">
              <div className="flex items-center justify-center mb-4">
                <div className="bg-primary rounded-lg p-2">
                  <Code className="h-6 w-6 text-white" />
                </div>{" "}
              </div>
              <h1 className="text-2xl font-bold">{t('auth.loginTitle')}</h1>
              <p className="text-gray-600 mt-2">
                {t('auth.loginSubtitle')}
              </p>
            </div>

            <Form
              method="post"
              className={`space-y-4 ${isSubmitting ? "animate-pulse" : ""}`}
            >
              <div className="space-y-2">
                <label
                  htmlFor="username"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t('auth.email')}
                </label>
                <input
                  id="username"
                  name="username"
                  type="email"
                  required
                  placeholder="ornek@email.com"
                  className="w-full h-11 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t('auth.password')}
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    required
                    placeholder="••••••••"
                    className="w-full h-11 px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                  <button
                    type="button"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-primary-300 rounded-r-md"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-end">
                {/* <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="remember"
                      className="rounded border-gray-300"
                    />
                    <label htmlFor="remember" className="text-sm text-gray-700">
                      Beni hatırla
                    </label>
                  </div> */}
                <Link
                  to="/forgot-password"
                  className="text-sm text-primary hover:text-primary-700"
                >
                  {t('auth.forgotPassword')}
                </Link>
              </div>

              <button
                disabled={isSubmitting}
                type="submit"
                className={`w-full h-11 text-base font-medium text-white rounded-md transition-colors duration-300 ${
                  isSubmitting
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-primary hover:bg-primary-600 hover:shadow-lg cursor-pointer"
                }`}
              >
                {isSubmitting ? t('auth.loggingIn') : t('auth.login')}
              </button>

              <Link to="/register">
                <Button className="w-full mt-2 text-base text-center hover:bg-primary-600 hover:shadow-lg transition-colors duration-300">
                  {t('auth.register')}
                </Button>
              </Link>
            </Form>
          </div>
        </div>

        {/* Coding Illustration Section */}
        <div className="bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center p-8 lg:p-12">
          <div className="relative w-full max-w-lg">
            {/* Main Illustration Container */}
            <div className="relative bg-white rounded-2xl shadow-xl p-6 border">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-2">
                  <Terminal className="h-5 w-5 text-primary" />
                  <span className="font-semibold text-gray-700">
                    {t('codeEditor.title')}
                  </span>
                </div>
                <div className="flex space-x-1">
                  <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                  <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                  <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                </div>
              </div>

              {/* Code Editor Mockup */}
              <div className="bg-gray-900 rounded-lg p-4 font-mono text-sm space-y-2">
                <div className="flex items-center space-x-2">
                  <span className="text-gray-500">1</span>
                  <span className="text-purple-400">function</span>
                  <span className="text-blue-400">learnCoding</span>
                  <span className="text-yellow-400">()</span>
                  <span className="text-white">{"{"}</span>
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  <span className="text-gray-500">2</span>
                  <span className="text-red-400">const</span>
                  <span className="text-blue-300">skills</span>
                  <span className="text-white">=</span>
                  <span className="text-green-400">['React', 'Node.js']</span>
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  <span className="text-gray-500">3</span>
                  <span className="text-purple-400">return</span>
                  <span className="text-green-400">'Başarı!'</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-gray-500">4</span>
                  <span className="text-white">{"}"}</span>
                </div>
              </div>

              {/* Eğitim Şekilleri */}
              <div className="grid grid-cols-3 gap-3 mt-6">
                <div className="text-center p-3 bg-primary-50 rounded-lg">
                  <Monitor className="h-5 w-5 text-primary-600 mx-auto mb-1" />
                  <div className="text-lg font-bold text-primary-600">
                    {t('education.online')}
                  </div>
                  <div className="text-xs text-primary-600">{t('education.training')}</div>
                </div>
                <div className="text-center p-3 bg-primary-100 rounded-lg">
                  <Users className="h-5 w-5 text-primary-700 mx-auto mb-1" />
                  <div className="text-lg font-bold text-primary-700">
                    {t('education.hybrid')}
                  </div>
                  <div className="text-xs text-primary-700">{t('education.training')}</div>
                </div>
                <div className="text-center p-3 bg-primary-200 rounded-lg">
                  <Building className="h-5 w-5 text-primary-800 mx-auto mb-1" />
                  <div className="text-lg font-bold text-primary-800">
                    {t('education.faceToFace')}
                  </div>
                  <div className="text-xs text-primary-800">{t('education.training')}</div>
                </div>
              </div>

              {/* Programming Languages */}
              <div className="mt-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  {t('technologies.popular')}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {[
                    "JavaScript",
                    "React",
                    "Node.js",
                    "Python",
                    "TypeScript",
                  ].map((tech) => (
                    <span
                      key={tech}
                      className="px-3 py-1 bg-gradient-to-r from-primary to-primary-600 text-white text-xs rounded-full font-medium"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Floating Elements */}
            <div className="absolute -top-3 -right-3 bg-primary-400 rounded-full p-2 shadow-lg animate-bounce">
              <Code className="h-5 w-5 text-primary-900" />
            </div>
            <div className="absolute -bottom-3 -left-3 bg-primary rounded-full p-2 shadow-lg animate-pulse">
              <Terminal className="h-5 w-5 text-white" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
